import type { ResultMessage } from "@copilotkit/runtime-client-gql";
import { safeJsonParse } from "@memorang/helpers/safe-json";
import {
	type ActionExecutionResult,
	COPILOT_ACTION_RENDERERS,
} from "../renderers";

type ActionName = keyof typeof COPILOT_ACTION_RENDERERS;

const FORBIDDEN_PROP_NAMES = new Set([
	"dangerouslySetInnerHTML",
	"style",
	"ref",
	"key",
	"children",
]);

const sanitizeProps = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return null;
	}
	const safe: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
		if (FORBIDDEN_PROP_NAMES.has(k) || k.startsWith("on")) {
			continue;
		}
		safe[k] = v;
	}
	return safe;
};

export const ActionRenderer = ({
	message,
}: {
	message: ResultMessage;
}) => {
	const validRenderer =
		COPILOT_ACTION_RENDERERS[message.actionName as ActionName];

	if (!validRenderer) {
		return null;
	}
	const executionResult: ActionExecutionResult | null =
		message.result === ""
			? { type: "error", message: "No result found" }
			: safeJsonParse<ActionExecutionResult>(message.result);

	if (!executionResult) {
		return null;
	}

	const { component: Component } = validRenderer;

	const safeProps = sanitizeProps(executionResult.result) ?? {};

	// Props are determined at runtime by the action name; static narrowing isn't possible here.
	// biome-ignore lint/suspicious/noExplicitAny: dynamic component dispatch
	return <Component {...(safeProps as any)} />;
};
